/**
 * Password field with a strength meter and an always-present rules checklist.
 *
 * Built to design/web/archetypes/auth.md. Replaces the old Progress + red Alert
 * + green Alert (which appeared and disappeared as you typed) with one 3px meter
 * and a fixed five-row checklist: nothing moves, the glyph shape carries the
 * state as well as the colour (rule 6), and the list announces changes to a
 * screen reader. The password generator dropdown is gone — two of its three
 * options were the same function and the third did nothing (W-44).
 *
 * Rendered on the reset and force-change screens and inside the signed-in
 * profile; the meter/checklist are correct in all three.
 */

import { Input } from 'antd';
import { IconHide, IconView, IconLock } from './icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validatePassword, evaluatePasswordRules } from '../../core/utils/passwordUtils';

// Three distinct shapes so the state reads without colour: met / failed / pending.
const CheckMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 13 4.5 4.5L19 7" /></svg>
);
const CrossMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const RingMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       aria-hidden="true"><circle cx="12" cy="12" r="8" /></svg>
);

// Score → one of three bands. The feed archetype's meter, three states.
const strengthBand = (score) => {
  if (score < 50) return { key: 'weak', color: 'var(--color-fn-garnet)' };
  if (score < 80) return { key: 'fair', color: 'var(--color-fn-saffron)' };
  return { key: 'strong', color: 'var(--color-fn-pistachio)' };
};

const PasswordStrengthInput = ({
  value,
  onChange,
  placeholder,
  showChecklist = true,
  ...props
}) => {
  const { t } = useTranslation();
  const [score, setScore] = useState(0);

  useEffect(() => {
    setScore(value ? validatePassword(value).score : 0);
  }, [value]);

  const rules = useMemo(() => evaluatePasswordRules(value || ''), [value]);
  const band = strengthBand(score);
  const hasValue = Boolean(value);

  return (
    <div>
      <Input.Password
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? t('auth.enterNewPassword')}
        prefix={<IconLock />}
        // The glyph is where a tap goes — an eye while the password is hidden —
        // the convention Login and the phone's fields share.
        iconRender={(visible) => (visible ? <IconHide /> : <IconView />)}
        size="large"
        autoComplete="new-password"
        {...props}
      />

      {showChecklist && (
        <>
          <div className="lms-auth__meter">
            <span className="lms-auth__meter-track">
              <span
                className="lms-auth__meter-fill"
                style={{ width: `${score}%`, backgroundColor: band.color }}
              />
            </span>
            {hasValue && (
              <span className="lms-auth__meter-word" style={{ color: band.color }}>
                {t(`auth.passwordStrength.${band.key}`)}
              </span>
            )}
          </div>

          <ul className="lms-auth__rules" aria-live="polite">
            {rules.map((rule) => {
              const state = !hasValue ? 'pending' : rule.met ? 'met' : 'failed';
              const stateWord = t(`auth.rule.${state}`);
              return (
                <li key={rule.key} className={`lms-auth__rule lms-auth__rule--${state}`}>
                  <span className="lms-auth__rule-glyph">
                    {state === 'met' ? <CheckMark /> : state === 'failed' ? <CrossMark /> : <RingMark />}
                  </span>
                  {t(`auth.passwordRules.${rule.key}`)}
                  <span className="lms-auth__sr">{stateWord}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

export default PasswordStrengthInput;
